<template>
	<base-modal
		ref="$modal"
		fullscreen-on-mobile
		:loading="isSubmitting"
		:title="title"
		width="940px"
		max-width="95vw"
		height="auto"
		max-height="90vh"
		@on-close="onModalClose"
	>
		<div
			ref="$content"
			class="stage-auto-jobs"
		>
			<div class="stage-auto-jobs__list">
				<div class="stage-auto-jobs__hint">{{ $t('autojob_dialog_hint') }}</div>

				<section
					v-if="fromTemplates.length"
					class="stage-auto-jobs__section"
				>
					<div class="stage-auto-jobs__section-title">
						{{ $t('autojob_section_from', { name: context?.fromStageName ?? '' }) }}
					</div>
					<auto-job-template-card
						v-for="state in fromTemplates"
						:key="state.key"
						:state="state"
						:errors="errorsByKey[state.key] ?? {}"
						@update="updateTemplate(state.key, $event)"
						@refresh-authors="refreshAuthors(state.key)"
						@refresh-executors="refreshExecutors(state.key)"
						@calendar-toggle="isCalendarOpen = $event"
					/>
				</section>

				<section
					v-if="toTemplates.length"
					class="stage-auto-jobs__section"
				>
					<div class="stage-auto-jobs__section-title">
						{{ $t('autojob_section_to', { name: context?.toStageName ?? '' }) }}
					</div>
					<auto-job-template-card
						v-for="state in toTemplates"
						:key="state.key"
						:state="state"
						:errors="errorsByKey[state.key] ?? {}"
						@update="updateTemplate(state.key, $event)"
						@refresh-authors="refreshAuthors(state.key)"
						@refresh-executors="refreshExecutors(state.key)"
						@calendar-toggle="isCalendarOpen = $event"
					/>
				</section>
			</div>

			<div class="stage-auto-jobs__divider" />
			<div class="stage-auto-jobs__footer">
				<app-button
					:label="$t('autojob_submit')"
					:disabled="!canSubmit"
					@click="submit"
				/>
			</div>
		</div>
	</base-modal>
</template>

<script setup lang="ts">
import {
	computed,
	ref
} from "vue";
import { i18n } from "@/i18n";
import BaseModal from "@/components/common/modal/BaseModal.vue";
import AppButton from "@/components/ui-kit/button/AppButton.vue";
import AutoJobTemplateCard from "@/components/main/project-block-page/modals/AutoJobTemplateCard.vue";
import { useModalEscape } from "@/composables/useModalEscape";
import { useStageAutoJobs } from "@/composables/useStageAutoJobs";
import type { StageAutoJobsPayload } from "@/composables/useStageAutoJobs";

const emit = defineEmits<{
	(e: 'closed'): void;
}>();

const $modal = ref<typeof BaseModal | null>(null);
const $content = ref<HTMLElement | null>(null);

const isCalendarOpen = ref(false);

const {
	context,
	fromTemplates,
	toTemplates,
	errorsByKey,
	isSubmitting,
	canSubmit,
	init,
	reset,
	updateTemplate,
	refreshAuthors,
	refreshExecutors,
	submit,
} = useStageAutoJobs({
	onClose: () => $modal.value?.onClose()
});

const { activate: activateEsc, deactivate: deactivateEsc } = useModalEscape({
	content: $content,
	close: () => $modal.value?.onClose(),
	intercept: () => isSubmitting.value || isCalendarOpen.value
});

const title = computed(() =>
	String(i18n.global.t('autojob_dialog_title', { name: context.value?.fromStageName ?? '' })));

const onOpen = (payload: StageAutoJobsPayload) => {
	init(payload);
	isCalendarOpen.value = false;
	$modal.value?.onOpen();
	activateEsc();
};

// Закрытие без отправки отменяет смену этапа: страница вернёт в селекте прежний этап
const onModalClose = () => {
	deactivateEsc();
	reset();
	isCalendarOpen.value = false;
	emit('closed');
};

defineExpose({ onOpen });
</script>

<style scoped lang="scss">
@import "@/styles/color.scss";

.stage-auto-jobs {
	display: flex;
	flex-direction: column;
	max-height: calc(90vh - 56px);

	&__list {
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: 12px;
		min-height: 0;
		padding: 12px;
		overflow-y: auto;
		overscroll-behavior: contain;
	}

	&__hint {
		font-size: 14px;
		line-height: 18px;
		color: $color-text_main;
	}

	&__section {
		display: flex;
		flex-direction: column;
		gap: 12px;

		&-title {
			font-weight: 500;
			font-size: 14px;
			line-height: 18px;
			color: $color-text_main;
		}
	}

	&__divider {
		flex-shrink: 0;
		height: 1px;
		margin: 0 12px;
		background-color: $color-border;
	}

	&__footer {
		display: flex;
		flex-shrink: 0;
		justify-content: flex-end;
		padding: 12px 12px max(12px, env(safe-area-inset-bottom));
	}
}
</style>

<style lang="scss">
.base-modal--fullscreen .stage-auto-jobs {
	height: 100%;
	max-height: none;
}
</style>
